(() => {
  if (customElements.get('broek-welcome')) return;
  const KEY = 'broek_welcome_winter26';
  const DAY = 86400000;
  const read = (area, key) => { try { return window[area].getItem(key); } catch (_) { return null; } };
  const write = (area, key, value) => { try { window[area].setItem(key, String(value)); } catch (_) {} };
  class BroekWelcome extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || typeof this.dialog.show !== 'function') return;
      this.abort = new AbortController();
      const opts = { signal: this.abort.signal };
      const params = new URLSearchParams(location.search);
      this.preview = Boolean(window.Shopify?.designMode) ||
        params.get('welcome_preview') === '1';
      this.key = KEY + (this.preview ? '_preview' : '');
      this.querySelectorAll('[data-welcome-close]').forEach(button => button.addEventListener('click', () => this.close(), opts));
      this.addEventListener('click', event => { if (event.target === this) this.close(); }, opts);
      this.dialog.addEventListener('click', event => { if (event.target === this.dialog) {
        const r = this.dialog.getBoundingClientRect();
        if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.close();
      } }, opts);
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); }, opts);
      this.dialog.addEventListener('keydown', event => {
        if (event.key === 'Escape') { event.preventDefault(); this.close(); return; }
        if (event.key !== 'Tab') return;
        const controls = [...this.dialog.querySelectorAll('button:not([disabled]),a[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex="0"]')].filter(el => el.getClientRects().length);
        const first = controls[0], last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }, opts);
      this.dialog.addEventListener('close', () => {
        this.classList.remove('bw-is-open');
        document.documentElement.classList.remove('bw-popup-open');
        this.previousFocus?.focus?.({preventScroll:true});
      }, opts);
      this.querySelector('[data-welcome-copy]')?.addEventListener('click', async event => {
        const button = event.currentTarget;
        try {
          await navigator.clipboard.writeText(button.dataset.code);
          button.textContent = 'Copied';
          this.status(this.preview ? 'Preview only. The code will activate at launch.' : 'Code copied. Enter it at checkout.');
        } catch (_) { this.status('Use WELCOME10 at checkout.'); }
      }, opts);
      this.querySelector('[data-welcome-claim]')?.addEventListener('click', event => {
        if (this.preview) { event.preventDefault(); this.status('Preview only. The code will activate at launch.'); return; }
        write('localStorage', this.key + '_until', Date.now() + 365 * DAY);
        this.close(false);
      }, opts);
      this.form = this.querySelector('.bw-form');
      if (this.form) {
        const back = new URL(location.href);
        back.searchParams.set('customer_posted', 'true');
        back.searchParams.set('welcome_signup', '1');
        back.hash = this.form.id;
        const returnTo = this.form.querySelector('[name="return_to"]');
        if (returnTo) returnTo.value = back.pathname + back.search + back.hash;
      }
      const rememberSignup = () => {
        if (window.Shopify?.designMode || !this.form?.checkValidity()) return;
        write('sessionStorage', this.key + '_pending', Date.now());
      };
      this.form?.querySelector('button[type="submit"]')?.addEventListener('click', rememberSignup, opts);
      this.form?.addEventListener('keydown', event => { if (event.key === 'Enter') rememberSignup(); }, opts);
      this.form?.addEventListener('submit', event => {
        if (!this.form.reportValidity()) { event.preventDefault(); return; }
        if (window.Shopify?.designMode) {
          event.preventDefault();
          this.status('Open the storefront preview to test email signup.');
          return;
        }
        rememberSignup();
      }, opts);
      const pending = Number(read('sessionStorage', this.key + '_pending'));
      const returned = params.get('welcome_signup') === '1' || location.hash === '#' + this.form?.id;
      const recentSignup = pending > 0 && Date.now() - pending < 60 * 60 * 1000;
      const success = this.querySelector('[data-welcome-success]');
      const failed = this.querySelector('[data-welcome-error]');
      if (recentSignup && (returned || failed)) {
        if (success && !failed) {
          this.querySelector('[data-welcome-fields]').hidden = true;
          this.querySelector('.bw-description').hidden = true;
          success.hidden = false;
          write('sessionStorage', this.key + '_pending', '0');
          write('localStorage', this.key + '_until', Date.now() + 365 * DAY);
          this.completed = true;
        }
        if (success || failed) {
          this.timer = setTimeout(() => this.open(), 0);
          return;
        }
      }
      document.addEventListener('shopify:section:select', event => {
        if (window.Shopify?.designMode && event.detail.sectionId === this.dataset.sectionId) this.open();
      }, opts);
      if (this.preview) { this.timer = setTimeout(() => this.open(), 1000); return; }
      this.schedule();
    }
    schedule() {
      const starts = Date.parse(this.dataset.start || '');
      if (this.dataset.ready !== 'true' || !Number.isFinite(starts)) return;
      if (Date.now() < starts) {
        this.timer = setTimeout(() => this.schedule(), Math.min(starts - Date.now(), 2147483647));
        return;
      }
      if (read('sessionStorage', this.key + '_shown') || Number(read('localStorage', this.key + '_until')) > Date.now()) return;
      const first = Number(read('sessionStorage', this.key + '_first')) || Date.now();
      write('sessionStorage', this.key + '_first', first);
      this.deadline = Date.now() + 10 * 60 * 1000;
      this.timer = setTimeout(() => this.tryOpen(), Math.max(0, Number(this.dataset.delay) * 1000 - (Date.now() - first)));
    }
    busy() {
      return document.hidden || /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement?.tagName || '') ||
        document.activeElement?.isContentEditable ||
        Boolean(document.querySelector('dialog[open],.tingle-modal--visible,.bls__drawer.active,.bls__drawer.open,.minicart.is-open,.bx-nav-desktop details[open]')) ||
        document.documentElement.classList.contains('bx-nav-locked') ||
        document.body.classList.contains('overflow-hidden');
    }
    tryOpen() {
      if (!this.isConnected || Date.now() > this.deadline) return;
      if (this.busy()) { this.timer = setTimeout(() => this.tryOpen(), 2000); return; }
      this.open();
    }
    open() {
      if (this.dialog.open || !this.isConnected) return;
      if (this.preview && document.querySelector('dialog[open]')) return;
      this.previousFocus = document.activeElement;
      this.classList.add('bw-is-open');
      this.dialog.show();
      document.documentElement.classList.add('bw-popup-open');
      write('sessionStorage', this.key + '_shown', '1');
      this.querySelector('[data-welcome-close]').focus({preventScroll:true});
    }
    close(remember = true) {
      clearTimeout(this.timer);
      if (remember && !this.completed) write('localStorage', this.key + '_until', Date.now() + 7 * DAY);
      this.dialog.close();
    }
    status(message) { this.querySelector('.bw-status').textContent = message; }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.abort?.abort();
      this.classList.remove('bw-is-open');
      if (this.dialog?.open) { this.dialog.close(); document.documentElement.classList.remove('bw-popup-open'); }
      this.bound = false;
    }
  }
  customElements.define('broek-welcome', BroekWelcome);
})();
